import { RemovableCard, Text } from '@dixa/lib-shared-habla';
import type { Queue } from 'lib-shared-types-api';
import { useEffect, useState } from 'react';

import { InputQueuesSelect } from 'components/inputQueuesSelect';

import {
  BulkModalActionBaseProps,
  BulkModalTransferToQueueAction,
} from '../../../types/conversationsBulkModal.types';
import { conversationsBulkModalActionLabel } from '../../../utils/conversationsBulkModalActionLabel';

type ConversationsBulkModalTransferToQueueActionProps =
  BulkModalActionBaseProps<BulkModalTransferToQueueAction>;

/**
 * Represents the component for the conversations bulk modal transfer to queue
 * action.
 */
export const ConversationsBulkModalTransferToQueueAction = ({
  conversationIds,
  onRemove,
  onChange,
}: ConversationsBulkModalTransferToQueueActionProps): JSX.Element => {
  const [selectedQueues, setSelectedQueues] = useState<Queue[]>([]);
  const selectedQueue: Queue | undefined = selectedQueues[0];

  useEffect(
    () =>
      onChange({
        type: 'transfer_to_queue',
        isValid: !!selectedQueue?.id,
        arg: {
          csids: conversationIds,
          // Note: returns empty string if the queue id is undefined, which also
          // means that the action would be invalid which would guard the
          // ability to perform bulk actions by the modal.
          queueId: selectedQueue?.id ?? '',
        },
      }),
    [conversationIds, onChange, selectedQueue?.id],
  );

  return (
    <RemovableCard
      icon="arrow-right"
      title={conversationsBulkModalActionLabel['transfer_to_queue']}
      onRemove={() => onRemove('transfer_to_queue')}
    >
      <Text.Paragraph subdued withMarginBottom>
        Transferring conversations to a different queue will cause all those
        conversations to open and be available for offering in the new queue. If
        you would like to avoid that and close the selected conversations as
        well, then you can create a new bulk action afterwards and use the
        “Close” action.
      </Text.Paragraph>

      <InputQueuesSelect
        value={selectedQueues}
        onChange={({ selectedValues }) => setSelectedQueues(selectedValues)}
      />
    </RemovableCard>
  );
};
